import React, { useEffect, useState, useCallback } from 'react';
import { getJob, getJobFiles } from '../../../request/transcription/transcription.api.ts';
import type { TranscriptionJob, TranscriptionOutputFile } from '../../../request/transcription/transcription.api.ts';

const POLL_INTERVAL_MS = 5000;

interface JobStatusCardProps {
  jobId: string;
  onReset: () => void;
}

export function JobStatusCard({ jobId, onReset }: JobStatusCardProps) {
  const [job, setJob] = useState<TranscriptionJob | null>(null);
  const [files, setFiles] = useState<TranscriptionOutputFile[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchJob = useCallback(async () => {
    try {
      const data = await getJob(jobId);
      setJob(data);

      if (data.status === 'completed' && files.length === 0) {
        const result = await getJobFiles(jobId);
        setFiles(result.files ?? []);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to fetch job status');
    }
  }, [jobId, files.length]);

  useEffect(() => {
    fetchJob();
    const interval = setInterval(() => {
      if (job?.status === 'completed' || job?.status === 'failed' || job?.status === 'cancelled') {
        clearInterval(interval);
        return;
      }
      fetchJob();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchJob, job?.status]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={onReset} className="mt-2 text-sm text-red-700 underline">Start over</button>
      </div>
    );
  }

  if (!job) {
    return <div className="text-sm text-gray-500 animate-pulse">Loading job status…</div>;
  }

  const statusColor: Record<string, string> = {
    queued:      'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed:   'bg-green-100 text-green-700',
    failed:      'bg-red-100 text-red-700',
    cancelled:   'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 font-mono">{jobId}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[job.status] ?? 'bg-gray-100 text-gray-700'}`}>
            {job.status.replace('_', ' ')}
          </span>
        </div>
        {(job.status === 'completed' || job.status === 'failed') && (
          <button
            onClick={onReset}
            className="text-sm text-indigo-600 hover:underline"
          >
            New job
          </button>
        )}
      </div>

      {/* Progress bar */}
      {(job.status === 'queued' || job.status === 'in_progress') && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{job.message ?? 'Processing…'}</span>
            <span>{job.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${job.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {job.status === 'failed' && job.error_message && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-700">{job.error_message}</p>
        </div>
      )}

      {/* Output files */}
      {job.status === 'completed' && files.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            {files.length} file{files.length !== 1 ? 's' : ''} ready
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {files.map(f => (
              <li key={f.gcs_ref} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                <span className="text-sm text-gray-700 truncate max-w-xs">{f.filename}</span>
                <a
                  href={f.signed_url}
                  download={f.filename}
                  className="ml-4 text-sm text-indigo-600 hover:underline shrink-0"
                >
                  Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {job.status === 'completed' && files.length === 0 && (
        <p className="text-sm text-gray-500">No output files found.</p>
      )}
    </div>
  );
}
